import cx from 'classnames';
import type { MouseEvent } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import * as EventAddOnIcons from '@assets/icons/event-add-ons';

import styles from './PeakLibraryIconPicker.scss';

const ICON_OPTIONS = [
  { name: 'wine', label: 'Wine', Icon: EventAddOnIcons.Wine },
  { name: 'champagne', label: 'Champagne', Icon: EventAddOnIcons.Champagne },
  { name: 'cocktails', label: 'Cocktails', Icon: EventAddOnIcons.Cocktails },
  {
    name: 'place-settings',
    label: 'Place Settings',
    Icon: EventAddOnIcons.PlaceSettings,
  },
  { name: 'platters', label: 'Platters', Icon: EventAddOnIcons.Platters },
  { name: 'dessert', label: 'Dessert', Icon: EventAddOnIcons.Dessert },
  {
    name: 'party-favors',
    label: 'Party Favors',
    Icon: EventAddOnIcons.PartyFavors,
  },
  {
    name: 'coffee-tea',
    label: 'Coffee/Tea',
    Icon: EventAddOnIcons.CoffeeTea,
  },
] as const;

export type PeakLibraryIconName = (typeof ICON_OPTIONS)[number]['name'];

interface PeakLibraryIconPickerProps {
  selectedIcon: PeakLibraryIconName | null;
  onSelect: (iconName: PeakLibraryIconName) => void;
}

export const PeakLibraryIconPicker = ({
  selectedIcon,
  onSelect,
}: PeakLibraryIconPickerProps) => {
  const handleClick = (
    mouseEvent: MouseEvent,
    iconName: PeakLibraryIconName,
  ) => {
    mouseEvent.preventDefault();
    onSelect(iconName);
  };

  return (
    <div
      role="radiogroup"
      aria-label="Select an icon from Peak Library"
      className={styles.iconGrid}
    >
      {ICON_OPTIONS.map(({ name, label, Icon }) => {
        const isSelected = name === selectedIcon;

        return (
          <div
            key={name}
            role="radio"
            aria-checked={isSelected}
            aria-label={label}
            className={cx(styles.iconOption, {
              [styles.selected]: isSelected,
            })}
            onClick={(mouseEvent) => handleClick(mouseEvent, name)}
          >
            <Icon className={styles.icon} />
            <span className={styles.label}>{label}</span>
          </div>
        );
      })}
    </div>
  );
};

export const getIconComponent = (
  iconName: PeakLibraryIconName,
): React.FunctionComponent<React.SVGAttributes<SVGElement>> | null => {
  const option = ICON_OPTIONS.find((opt) => opt.name === iconName);
  return option?.Icon ?? null;
};

export const renderIconToSvgString = (
  iconName: PeakLibraryIconName,
): string => {
  const IconComponent = getIconComponent(iconName);
  if (!IconComponent) {
    throw new Error(`Icon not found: ${iconName}`);
  }
  return renderToStaticMarkup(<IconComponent />);
};
